import { z } from 'zod';

/**
 * MCP (Model Context Protocol) — JSON-RPC контракт между AI Core и
 * Projoint.  AI Core читает контекст и (Phase 3+) делает действия от
 * имени user через PAT.
 *
 * См. docs/ai-platform/02-ARCHITECTURE.md § 8.
 *
 * Phase 2 — first chunk: read-only resources (tasks, projects).
 * Phase 3 — tools/call (write operations).
 */

// ── JSON-RPC 2.0 envelope ─────────────────────────────────────────────────

export const McpMethod = z.enum([
  'initialize',
  'resources/list',
  'resources/read',
  'tools/list',
  'tools/call',
]);
export type McpMethodValue = z.infer<typeof McpMethod>;

export const McpRequestSchema = z.object({
  jsonrpc: z.literal('2.0'),
  id: z.union([z.string(), z.number()]),
  method: McpMethod,
  params: z.record(z.string(), z.unknown()).optional(),
});
export type McpRequest = z.infer<typeof McpRequestSchema>;

export const McpErrorSchema = z.object({
  code: z.number().int(),
  message: z.string(),
  data: z.unknown().optional(),
});
export type McpError = z.infer<typeof McpErrorSchema>;

export const McpResponseSchema = z.object({
  jsonrpc: z.literal('2.0'),
  id: z.union([z.string(), z.number(), z.null()]),
  result: z.unknown().optional(),
  error: McpErrorSchema.optional(),
});
export type McpResponse = z.infer<typeof McpResponseSchema>;

// ── initialize response ────────────────────────────────────────────────────

export const McpServerInfoSchema = z.object({
  name: z.string(),
  version: z.string(),
});
export type McpServerInfo = z.infer<typeof McpServerInfoSchema>;

export const McpCapabilitiesSchema = z.object({
  resources: z
    .object({
      subscribe: z.boolean().default(false),
      listChanged: z.boolean().default(false),
    })
    .default({ subscribe: false, listChanged: false }),
  tools: z
    .object({
      listChanged: z.boolean().default(false),
    })
    .default({ listChanged: false }),
  extensions: z
    .object({
      toolSchemaVersion: z.number().int().positive(),
    })
    .optional(),
});
export type McpCapabilities = z.infer<typeof McpCapabilitiesSchema>;

export const McpInitializeResultSchema = z.object({
  protocolVersion: z.string(),
  serverInfo: McpServerInfoSchema,
  capabilities: McpCapabilitiesSchema,
});
export type McpInitializeResult = z.infer<typeof McpInitializeResultSchema>;

// ── resources ──────────────────────────────────────────────────────────────

/**
 * Per-resource URI scheme.  Resource implementations match exact URIs:
 *   projoint://tasks                  — list tasks visible to actor
 *   projoint://tasks/{taskId}         — single task
 *   projoint://projects               — list projects visible to actor
 *   projoint://projects/{projectId}   — single project
 *
 * Phase 2+ adds: chat topics, inbox items, files, search.
 */
export const McpResourceSchema = z.object({
  uri: z.string(),
  name: z.string(),
  description: z.string().optional(),
  mimeType: z.string().default('application/json'),
});
export type McpResource = z.infer<typeof McpResourceSchema>;

export const McpResourcesListResultSchema = z.object({
  resources: z.array(McpResourceSchema),
});
export type McpResourcesListResult = z.infer<typeof McpResourcesListResultSchema>;

export const McpResourceReadResultSchema = z.object({
  contents: z.array(
    z.object({
      uri: z.string(),
      mimeType: z.string(),
      text: z.string(),
    }),
  ),
});
export type McpResourceReadResult = z.infer<typeof McpResourceReadResultSchema>;

// ── tools (Phase 3 stubs) ──────────────────────────────────────────────────

export const McpToolSchema = z.object({
  name: z.string(),
  description: z.string(),
  inputSchema: z.record(z.string(), z.unknown()),
});
export type McpTool = z.infer<typeof McpToolSchema>;

export const McpToolsListResultSchema = z.object({
  tools: z.array(McpToolSchema),
});
export type McpToolsListResult = z.infer<typeof McpToolsListResultSchema>;

/**
 * Tools/call params + result.  Phase 2 ships read-only tools (e.g.
 * `semantic_search` — vector similarity over knowledge chunks).  Phase 3
 * adds write tools alongside the agent framework; the result shape is
 * the same — content blocks + optional isError flag — so clients can
 * be written once.
 *
 * `arguments` is a free-form record because each tool has its own
 * input schema (declared in `tools/list`).  Validation is the tool
 * handler's responsibility.
 */
export const McpToolCallParamsSchema = z.object({
  name: z.string().min(1),
  arguments: z.record(z.string(), z.unknown()).optional(),
  flowContext: z.object({
    version: z.literal('task-local-v1'),
    runId: z.string().uuid(),
    policyHash: z.string().regex(/^[a-f0-9]{64}$/),
    contextRevision: z.string().regex(/^(0|[1-9][0-9]*)$/).refine(
      value => /^(0|[1-9][0-9]*)$/.test(value) && BigInt(value) <= 9223372036854775807n,
    ),
    readSetDigest: z.string().regex(/^[a-f0-9]{64}$/),
  }).strict().optional(),
  idempotencyKey: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:/-]*$/, 'invalid idempotency key')
    .optional(),
});
export type McpToolCallParams = z.infer<typeof McpToolCallParamsSchema>;

/**
 * Tool result content blocks.  MCP spec supports several block types
 * (text, image, resource_link, …); we ship text-only for now — JSON
 * payloads ride inside a single text block, which keeps the client
 * code identical to resources/read.
 */
export const McpToolContentBlockSchema = z.object({
  type: z.literal('text'),
  text: z.string(),
});
export type McpToolContentBlock = z.infer<typeof McpToolContentBlockSchema>;

export const McpToolCallResultSchema = z.object({
  content: z.array(McpToolContentBlockSchema),
  isError: z.boolean().default(false),
  flowContext: McpToolCallParamsSchema.shape.flowContext,
});
export type McpToolCallResult = z.infer<typeof McpToolCallResultSchema>;
